import type { Post, Project, ProjectContent, Tag } from "../types";

export const fallbackTags: Tag[] = [
  { id: "html", name: "HTML", slug: "html", icon_name: "html", icon_color: "#e56b2f" },
  { id: "css", name: "CSS", slug: "css", icon_name: "css", icon_color: "#2f8fe8" },
  { id: "javascript", name: "JavaScript", slug: "javascript", icon_name: "js", icon_color: "#e5b84e" },
  { id: "unity", name: "Unity", slug: "unity", icon_name: "cube", icon_color: "#d8dbe5" },
  { id: "godot", name: "Godot", slug: "godot", icon_name: "game", icon_color: "#55df7a" },
  { id: "games", name: "Games", slug: "games", icon_name: "game", icon_color: "#ff6473" },
  { id: "web", name: "Web", slug: "web", icon_name: "globe", icon_color: "#a970ff" },
  { id: "projetos", name: "Projects", slug: "projetos", icon_name: "code", icon_color: "#55df7a" }
];

const tagBySlug = Object.fromEntries(fallbackTags.map((tag) => [tag.slug, tag])) as Record<string, Tag>;

const projectContent: Record<string, ProjectContent> = {
  "unity-super-road": {
    eyebrow: "Unity",
    body: ["The \"Unity Super Road\" project is an exciting endless runner game, showcasing advanced development techniques using the Unity engine."],
    features: [
      { title: "Endless Gameplay", description: "Navigate through dynamically generated roads and obstacles." },
      { title: "High Scores", description: "Compete to achieve the best score with increasing difficulty." },
      { title: "Vibrant Graphics", description: "Engaging visuals that enhance the player experience." }
    ],
    concepts: [
      { title: "Unity Engine", description: "Utilizing Unity's 3D features to create immersive gameplay." },
      { title: "C# Scripting", description: "Implementing mechanics, score tracking, and procedural generation." },
      { title: "Optimization", description: "Ensuring smooth performance across devices." }
    ],
    source_url: "https://github.com/0xNunoMiranda/Unity.Super-Road"
  },
  "godot-duck-hunt": {
    eyebrow: "Godot",
    body: ["The \"Godot Duck Hunt\" project is a modern recreation of the classic arcade game, demonstrating game development skills using the Godot engine."],
    features: [
      { title: "Interactive Gameplay", description: "Test your aim in a fun and challenging duck-shooting game." },
      { title: "Dynamic Difficulty", description: "Ducks move faster as the game progresses." },
      { title: "Retro Vibes", description: "Captures the nostalgic feel of the original Duck Hunt game." }
    ],
    concepts: [
      { title: "Godot Engine", description: "Leveraging Godot's 2D capabilities for game mechanics and animations." },
      { title: "GDScript", description: "Implementing game logic, scoring, and dynamic behavior." },
      { title: "UI Design", description: "Creating an engaging interface with retro-themed visuals." }
    ],
    source_url: "https://github.com/0xNunoMiranda/Godot-DuckHunt"
  },
  "godot-rock-paper-scissors": {
    eyebrow: "Godot",
    body: ["The \"Godot Rock Paper Scissors\" project highlights a fun and interactive game built with Godot, showcasing fundamental concepts of game development."],
    features: [
      { title: "Interactive Gameplay", description: "Engage in a classic rock-paper-scissors duel." },
      { title: "Simple UI", description: "User-friendly interface for seamless interaction." },
      { title: "Lightweight Build", description: "Optimized for fast performance and smooth experience." }
    ],
    concepts: [
      { title: "Godot Engine", description: "Utilizing nodes and signals for event-driven programming." },
      { title: "GDScript", description: "Core logic for managing game mechanics." },
      { title: "UI Design", description: "Implementation of buttons, labels, and animations for enhanced user experience." }
    ],
    source_url: "https://github.com/0xNunoMiranda/Godot.Rock-Paper-Scissors"
  },
  "expanding-cards": {
    eyebrow: "HTML · CSS · JS",
    body: ["The \"Expanding Cards JS\" project showcases an interactive layout built with HTML, CSS, and JavaScript, where each card expands to display more details in a simple and dynamic way."],
    features: [
      { title: "Interactive Cards", description: "Smooth expansion upon clicking, revealing additional information." },
      { title: "Easy Navigation", description: "Intuitive interface focused on user experience." },
      { title: "Responsive Design", description: "Adapts perfectly to different devices and screen sizes." }
    ],
    concepts: [
      { title: "HTML Structure", description: "Semantic code for better organization and readability." },
      { title: "CSS Styling", description: "Control of layout, colors, and typography." },
      { title: "JavaScript", description: "Event handling for animations and interactivity." }
    ],
    source_url: "https://github.com/0xNunoMiranda/expanding-cards-js"
  },
  "travelprofile-simple-html-page": {
    eyebrow: "HTML · CSS",
    body: ["The \"Travel Profile Simple HTML Page\" is a lightweight web page, built with pure HTML and CSS, focused on displaying a travel profile in a simple and appealing way for users."],
    features: [
      { title: "Travel Profile", description: "Highlights destinations, places, and travel experiences." },
      { title: "Travel Tips", description: "Provides practical suggestions and recommendations for travelers." },
      { title: "Responsive Layout", description: "Easily adapts to various devices." }
    ],
    concepts: [
      { title: "HTML Structure", description: "Semantic code for better organization and readability." },
      { title: "CSS Styling", description: "Customizable styles to enhance the visual aspect." },
      { title: "Best Practices", description: "Use of fonts, colors, and icons to improve usability." }
    ],
    source_url: "https://github.com/0xNunoMiranda/travelprofile-simple-html-page"
  },
  "tourism-simple-html-page": {
    eyebrow: "HTML · CSS",
    body: ["The \"Tourism Simple HTML Page\" is a lightweight, user-friendly web page built with pure HTML and CSS, designed to showcase tourism destinations in a visually appealing way."],
    features: [
      { title: "Destination Highlights", description: "Showcases key points of interest in Busan." },
      { title: "Travel Tips", description: "Provides practical tips for travelers." },
      { title: "Visual Content", description: "Beautiful images to inspire your next trip." }
    ],
    concepts: [
      { title: "HTML Structure", description: "Semantic and organized code for better readability." },
      { title: "CSS Styling", description: "Custom styles to improve visual appearance." },
      { title: "Responsive Design", description: "Ensuring the page looks great on all devices." }
    ],
    source_url: "https://github.com/0xNunoMiranda/tourism-simple-html-page"
  },
  "recipe-simple-html-page": {
    eyebrow: "HTML · CSS",
    body: ["The \"Simple Recipe Page\" is a web page built with pure HTML, designed to present a recipe in a clear, organized, and visually appealing way."],
    features: [
      { title: "Recipe Title", description: "Clearly and objectively displayed." },
      { title: "Ingredient List", description: "Structured to facilitate the preparation process." },
      { title: "Step-by-Step Instructions", description: "Clear instructions to ensure the recipe can be easily followed." }
    ],
    concepts: [
      { title: "HTML Structure", description: "Semantic and hierarchical organization of content." },
      { title: "CSS Styling", description: "Customizing the design to improve visual experience." },
      { title: "Visual Hierarchy", description: "Fonts, spacing, images, lists, colors and text weights organized for readability." }
    ],
    source_url: "https://github.com/0xNunoMiranda/recipe-simple-html-page"
  }
};

export const fallbackPosts: Post[] = [
  {
    id: "travel-profile-simple-html-page",
    title: "Travel Profile Simple HTML Page",
    slug: "travel-profile-simple-html-page",
    summary: "A simple HTML and CSS project showcasing a travel profile, favourite destinations and experiences.",
    content: projectContent["travelprofile-simple-html-page"].body.join("\n\n"),
    post_type: "project",
    cover_image_url: "/api/images/articles/2026/16474fd6-2744-4cdf-8281-7aeb793b46a4.png",
    github_url: "https://github.com/0xNunoMiranda/travelprofile-simple-html-page",
    demo_url: "https://0xnunomiranda.github.io/travelprofile-simple-html-page/",
    status: "published",
    published_at: "2025-01-09T13:42:00.001+00:00",
    created_at: "2025-01-09T13:42:00.001+00:00",
    updated_at: "2025-01-09T13:42:18.196+00:00",
    tags: [tagBySlug.html, tagBySlug.css, tagBySlug.web]
  },
  {
    id: "2-simple-projects-in-html",
    title: "2 simple projects in HTML",
    slug: "2-simple-projects-in-html",
    summary: "Back to HTML basics with two simple projects: a recipe and a tourism page.",
    content: "An end-of-year note about revisiting HTML fundamentals and using a blog as a portfolio and technical resume.",
    post_type: "article",
    cover_image_url: "/api/images/articles/2026/e272ab95-c4c4-4120-b7f9-f5b27258f916.png",
    github_url: null,
    demo_url: "https://0xnunomiranda.github.io/tourism-simple-html-page/",
    status: "published",
    published_at: "2024-12-31T17:43:00.003+00:00",
    created_at: "2024-12-31T17:43:00.003+00:00",
    updated_at: "2025-01-09T13:42:43.651+00:00",
    tags: [tagBySlug.html, tagBySlug.css, tagBySlug.web, tagBySlug.projetos]
  }
];

export const fallbackProjects: Project[] = [
  {
    id: "unity-super-road",
    title: "Unity - Super Road",
    slug: "unity-super-road",
    summary: "An endless runner built with Unity, featuring procedural roads, obstacles and high-score challenges.",
    cover_image_url: "/api/images/articles/2026/f4a9fb45-9dab-4cb6-a52d-ecf1dac5d094.png",
    github_url: null,
    demo_url: "https://0xnunomiranda.github.io/Unity.Super-Road/",
    source_post_url: null,
    status: "published",
    created_at: "2025-01-23T01:30:32.000+00:00",
    updated_at: "2025-01-23T01:30:32.000+00:00",
    tags: [tagBySlug.unity, tagBySlug.games, tagBySlug.projetos],
    content: projectContent["unity-super-road"]
  },
  {
    id: "godot-duck-hunt",
    title: "Godot - Duck Hunt",
    slug: "godot-duck-hunt",
    summary: "A modern take on an arcade classic, showcasing game development skills in Godot.",
    cover_image_url: "/api/images/articles/2026/4223eb67-6ca2-404a-92fb-3b0d3017f5c7.png",
    github_url: null,
    demo_url: "https://0xnunomiranda.github.io/Godot-DuckHunt/build",
    source_post_url: null,
    status: "published",
    created_at: "2025-01-22T21:09:27.000+00:00",
    updated_at: "2025-01-22T21:09:27.000+00:00",
    tags: [tagBySlug.godot, tagBySlug.games, tagBySlug.projetos],
    content: projectContent["godot-duck-hunt"]
  },
  {
    id: "godot-rock-paper-scissors",
    title: "Godot - Rock Paper Scissors",
    slug: "godot-rock-paper-scissors",
    summary: "A simple Godot game based on the classic rock, paper, scissors.",
    cover_image_url: "/api/images/articles/2026/971e6c21-64f6-4fa6-b7d6-486651cb3db0.png",
    github_url: null,
    demo_url: "https://0xnunomiranda.github.io/Godot.Rock-Paper-Scissors/build/",
    source_post_url: null,
    status: "published",
    created_at: "2025-01-22T20:05:37.000+00:00",
    updated_at: "2025-01-22T20:05:37.000+00:00",
    tags: [tagBySlug.godot, tagBySlug.games, tagBySlug.projetos],
    content: projectContent["godot-rock-paper-scissors"]
  },
  {
    id: "expanding-cards",
    title: "Expanding Cards",
    slug: "expanding-cards",
    summary: "An interactive expanding-card interface built with JavaScript.",
    cover_image_url: "/api/images/articles/2026/c83c40f4-d9c7-4c4b-8b41-251ca99741c2.jpg",
    github_url: null,
    demo_url: "https://0xnunomiranda.github.io/expanding-cards-js/",
    source_post_url: null,
    status: "published",
    created_at: "2025-01-09T22:20:21.000+00:00",
    updated_at: "2025-01-09T22:20:21.000+00:00",
    tags: [tagBySlug.javascript, tagBySlug.css, tagBySlug.web, tagBySlug.projetos],
    content: projectContent["expanding-cards"]
  },
  {
    id: "travelprofile-simple-html-page",
    title: "Simple Travel Profile Page",
    slug: "travelprofile-simple-html-page",
    summary: "An intuitive travel profile page with an Instagram-inspired visual experience.",
    cover_image_url: "/api/images/articles/2026/16474fd6-2744-4cdf-8281-7aeb793b46a4.png",
    github_url: "https://github.com/0xNunoMiranda/travelprofile-simple-html-page",
    demo_url: "https://0xnunomiranda.github.io/travelprofile-simple-html-page/",
    source_post_url: null,
    status: "published",
    created_at: "2025-01-09T13:42:00.001+00:00",
    updated_at: "2025-01-09T13:42:18.196+00:00",
    tags: [tagBySlug.html, tagBySlug.css, tagBySlug.web, tagBySlug.projetos],
    content: projectContent["travelprofile-simple-html-page"]
  },
  {
    id: "tourism-simple-html-page",
    title: "Simple Tourism Page",
    slug: "tourism-simple-html-page",
    summary: "A minimalist tourism page built with HTML.",
    cover_image_url: "/api/images/articles/2026/2f3bb7d9-4a13-4ef1-90ca-962e86ca7e23.jpg",
    github_url: null,
    demo_url: "https://0xnunomiranda.github.io/tourism-simple-html-page/",
    source_post_url: null,
    status: "published",
    created_at: "2024-12-31T17:43:00.003+00:00",
    updated_at: "2025-01-09T13:42:43.651+00:00",
    tags: [tagBySlug.html, tagBySlug.css, tagBySlug.web, tagBySlug.projetos],
    content: projectContent["tourism-simple-html-page"]
  },
  {
    id: "recipe-simple-html-page",
    title: "Simple Recipe Page",
    slug: "recipe-simple-html-page",
    summary: "An intuitive, minimalist recipe page.",
    cover_image_url: "/api/images/articles/2026/0df497cc-b894-446d-9b17-cd93becd5c09.jpg",
    github_url: null,
    demo_url: "https://0xnunomiranda.github.io/recipe-simple-html-page/",
    source_post_url: null,
    status: "published",
    created_at: "2024-12-31T17:43:00.003+00:00",
    updated_at: "2025-01-09T13:42:43.651+00:00",
    tags: [tagBySlug.html, tagBySlug.css, tagBySlug.web, tagBySlug.projetos],
    content: projectContent["recipe-simple-html-page"]
  }
];
